import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchHistoricalData, fetchRecentReadingsRaw,
    fetchMonitorTemplates, createMonitorTemplate, deleteMonitorTemplate,
} from '../../../services/api';
import { Zone } from '../../../types';
import { NIBE_STATUS_PARAM_IDS, decodeNibeStatus } from '../../../utils/nibeStatus';
import {
    SurveillanceDevice, DataRow, MonitorRow, PropertyTemplate,
    ALL_PROPERTIES, PROPERTY_LABELS, toLocalDT, makeSeriesKey,
} from '../surveillanceShared';
import { useDeviceProperties } from './useDeviceProperties';

interface Options {
    zones: Zone[];
    devices: SurveillanceDevice[];
    /** Sant när fliken Översikt visas – bara då hämtas nya värden automatiskt. */
    active: boolean;
    onError: (message: string) => void;
}

// Mätvärden som aldrig visas i översiktens väljare (finns under Data i stället)
const MONITOR_EXCLUDED_PROPERTIES = new Set(['battery', 'battery_voltage', 'linkquality']);
const DEFAULT_PROPERTIES = ALL_PROPERTIES.filter(p => !MONITOR_EXCLUDED_PROPERTIES.has(p));

/**
 * Allt state för fliken Översikt: valda zon/enheter/mätvärden, senaste värden,
 * historik för diagrammet och mallar.
 */
export function useMonitorState({ zones, devices, active, onError }: Options) {
    // ── Urval ────────────────────────────────────────────────────────────────
    const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
    const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set(ALL_PROPERTIES));
    const [selectedZone, setSelectedZone] = useState<string>('');  // '' = alla zoner

    // ── Senaste värden ───────────────────────────────────────────────────────
    const [monitorData, setMonitorData] = useState<DataRow[]>([]);
    const [monitorLoading, setMonitorLoading] = useState(false);
    const [monitorError, setMonitorError] = useState<string | null>(null);

    // ── Historik / diagram ───────────────────────────────────────────────────
    const [logFrom, setLogFrom] = useState<string>(() => toLocalDT(new Date(Date.now() - 24 * 3600 * 1000)));
    const [logTo,   setLogTo]   = useState<string>(() => toLocalDT(new Date()));
    const [historyData, setHistoryData] = useState<DataRow[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);

    const deviceProps = useDeviceProperties();
    const { propertiesMap: devicePropertiesMap, labelMap: extraLabelMap, ensureLoaded } = deviceProps;

    // Hämta enheternas mätvärden så fort urvalet ändras
    useEffect(() => { ensureLoaded(Array.from(selectedDevices)); }, [selectedDevices, ensureLoaded]);

    // ── Namn på mätvärden ────────────────────────────────────────────────────
    // nyckel → namn, hämtat ur laddade rader
    const propNameMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const row of [...monitorData, ...historyData]) {
            if (row.parameter_name && !map[row.property]) map[row.property] = row.parameter_name;
        }
        return map;
    }, [monitorData, historyData]);

    // Läsbart namn för en nyckel (t.ex. "p_62005" → "Status")
    const propLabel = useCallback((prop: string, _ieee?: string): string => {
        if (PROPERTY_LABELS[prop]) return PROPERTY_LABELS[prop];
        if (propNameMap[prop]) return propNameMap[prop];
        if (extraLabelMap[prop]) return extraLabelMap[prop];
        return prop;
    }, [propNameMap, extraLabelMap]);

    // ── Mallar ───────────────────────────────────────────────────────────────
    const [templates, setTemplates] = useState<PropertyTemplate[]>([]);

    // Hämtas en gång; onError hålls i en ref så en ny funktion inte hämtar om.
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    useEffect(() => {
        fetchMonitorTemplates()
            .then(setTemplates)
            .catch((e: any) => onErrorRef.current(e.message || 'Kunde inte hämta mallarna'));
    }, []);

    const handleSaveTemplate = useCallback(async (name: string) => {
        const selectedDevicesList = Array.from(selectedDevices).map(id => {
            const dev = devices.find(d => d.id === id);
            return { id, name: dev?.name ?? id, type: dev?.type ?? '' };
        });
        const uniqueTypes = new Set(selectedDevicesList.map(d => d.type).filter(Boolean));
        const template_type: 'device_type' | 'multi_device' = uniqueTypes.size === 1 ? 'device_type' : 'multi_device';
        const device_type = template_type === 'device_type' ? Array.from(uniqueTypes)[0] : null;

        try {
            const created = await createMonitorTemplate({
                name,
                template_type,
                device_type: device_type ?? null,
                properties: Array.from(selectedProperties),
                devices: selectedDevicesList.map(d => ({ id: d.id, name: d.name })),
            });
            setTemplates(prev => [created, ...prev]);
        } catch (e: any) {
            onError(e.message || 'Kunde inte spara mallen');
        }
    }, [selectedProperties, selectedDevices, devices, onError]);

    const handleApplyTemplate = useCallback((id: number) => {
        const t = templates.find(tpl => tpl.id === id);
        if (!t) return;
        if (t.devices && t.devices.length > 0) {
            setSelectedZone('');
            setSelectedDevices(new Set(t.devices.map(d => d.id)));
        }
        setSelectedProperties(new Set(t.properties));
    }, [templates]);

    const handleDeleteTemplate = useCallback(async (id: number) => {
        try {
            await deleteMonitorTemplate(id);
            setTemplates(prev => prev.filter(t => t.id !== id));
        } catch (e: any) {
            onError(e.message || 'Kunde inte ta bort mallen');
        }
    }, [onError]);

    // Vilka mallar som visas beror på urvalet:
    //   • ingen enhet vald  → mallar för flera enheter
    //   • enheter valda     → enhetstypsmallar som matchar de valda typerna
    const visibleTemplates = useMemo(() => {
        if (selectedDevices.size === 0) {
            return templates.filter(t => t.template_type === 'multi_device');
        }
        const selectedTypes = new Set(
            Array.from(selectedDevices)
                .map(id => devices.find(d => d.id === id)?.type)
                .filter((t): t is string => Boolean(t))
        );
        return templates.filter(
            t => t.template_type === 'device_type' && t.device_type != null && selectedTypes.has(t.device_type)
        );
    }, [templates, selectedDevices, devices]);

    // ── Zon → enhetsfilter ───────────────────────────────────────────────────

    /** Enheter i vald zon (tom mängd = inget filter). */
    const zoneDeviceIds = useMemo<Set<string>>(() => {
        if (!selectedZone) return new Set<string>();
        const zone = zones.find(z => String(z.id) === selectedZone);
        if (!zone) return new Set<string>();
        return new Set(zone.devices.map(d => d.ieee_address));
    }, [selectedZone, zones]);

    /** Enheter som syns med nuvarande zonfilter. */
    const filteredDevices = useMemo(() => {
        if (!selectedZone) return devices;
        return devices.filter(d => zoneDeviceIds.has(d.id));
    }, [devices, selectedZone, zoneDeviceIds]);

    // När zonen byts: behåll bara valda enheter som fortfarande syns
    useEffect(() => {
        if (!selectedZone) return;
        setSelectedDevices(prev => {
            const next = new Set<string>();
            prev.forEach(id => { if (zoneDeviceIds.has(id)) next.add(id); });
            return next;
        });
    }, [selectedZone, zoneDeviceIds]);

    // ── Hämta senaste värden ─────────────────────────────────────────────────

    const loadMonitorData = useCallback(async () => {
        if (selectedDevices.size === 0 || selectedProperties.size === 0) { setMonitorData([]); return; }
        setMonitorLoading(true);
        setMonitorError(null);
        try {
            setMonitorData(await fetchRecentReadingsRaw(Array.from(selectedDevices), Array.from(selectedProperties)));
        } catch (e: any) {
            const msg = e?.code === 'ECONNABORTED'
                ? 'Det tog för lång tid att hämta värdena — välj färre enheter eller mätvärden.'
                : (e?.response?.data?.message ?? e?.message ?? 'Kunde inte hämta värdena');
            setMonitorError(msg);
            console.error('Monitor fetch error:', e);
        } finally {
            setMonitorLoading(false);
        }
    }, [selectedDevices, selectedProperties]);

    // Hämta när fliken visas eller enheterna ändras (ingen automatisk uppdatering)
    useEffect(() => {
        if (!active || selectedDevices.size === 0) return;
        loadMonitorData();
    }, [active, loadMonitorData, selectedDevices]);

    // ── Hämta historik ───────────────────────────────────────────────────────

    const loadHistoryData = useCallback(async () => {
        if (selectedDevices.size === 0) return;
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const props = selectedProperties.size > 0 ? Array.from(selectedProperties) : undefined;
            // logFrom/logTo är lokal väggklocka (från datetime-local). Databasen lagrar UTC,
            // så vi gör om till UTC-tidpunkter innan frågan – annars hamnar frågan fel
            // med användarens tidszonsförskjutning och korta perioder blir tomma.
            const fromISO = new Date(logFrom).toISOString();
            const toISO = new Date(logTo).toISOString();
            const rows = await fetchHistoricalData(Array.from(selectedDevices), fromISO, toISO, props);
            setHistoryData(Array.isArray(rows) ? rows : []);
        } catch (e: any) {
            const msg = e?.code === 'ECONNABORTED'
                ? 'Det tog för lång tid att hämta diagrammet — välj en kortare period eller färre mätvärden.'
                : (e?.response?.data?.message ?? e?.message ?? 'Kunde inte hämta diagramdata');
            setHistoryError(msg);
            console.error('History fetch error:', e);
        } finally {
            setHistoryLoading(false);
        }
    }, [selectedDevices, logFrom, logTo, selectedProperties]);

    // ── Bygg diagramserier av historiken ─────────────────────────────────────

    const chart = useMemo(() => {
        const empty = {
            chartData: [] as Record<string, any>[],
            chartSeries: [] as string[],
            // visningstext per serie och tidpunkt, för tooltip
            chartDisplayMap: {} as Record<string, string>,
            // serier med uppräkningsvärden (ritas mot höger y-axel)
            chartEnumSeries: new Set<string>(),
            // etiketter för höger y-axel: index → text
            chartEnumLabels: [] as string[],
        };
        if (historyData.length === 0) return empty;

        const pointMap = new Map<number, Record<string, any>>();
        const seriesSet = new Set<string>();
        const enumSeriesKeys = new Set<string>();
        const displayMap: Record<string, string> = {};

        // Första varvet: samla alla uppräkningsetiketter i de valda serierna
        const enumLabelSet = new Set<string>();
        for (const row of historyData) {
            if (!selectedDevices.has(row.ieee_address)) continue;
            if (!selectedProperties.has(row.property)) continue;
            if (row.enum_values?.length) {
                for (const e of row.enum_values) enumLabelSet.add(e.text);
            }
        }
        const enumLabels = Array.from(enumLabelSet).sort();
        const enumLabelIndex = new Map<string, number>();
        enumLabels.forEach((lbl, i) => enumLabelIndex.set(lbl, i));

        // Andra varvet: bygg punkterna
        for (const row of historyData) {
            if (!selectedDevices.has(row.ieee_address)) continue;
            if (!selectedProperties.has(row.property)) continue;
            const ts = new Date(row.timestamp).getTime();
            const enumVals = row.enum_values;
            // DECIMAL-kolumner kommer som strängar från pg – gör om till tal.
            const rowValueNum: number | null = row.value_num != null ? Number(row.value_num) : null;
            let numVal: number | undefined;
            let displayStr: string | undefined;
            let isEnum = false;

            if (enumVals?.length && rowValueNum != null) {
                const match = enumVals.find(e => String(e.value) === String(rowValueNum));
                const matchText = match?.text;
                const idx = matchText !== undefined ? enumLabelIndex.get(matchText) : undefined;
                if (idx !== undefined) {
                    // Riktig träff – rita på höger y-axel med etikettens index
                    isEnum = true;
                    numVal = idx;
                    displayStr = matchText;
                } else {
                    // enum_values finns men värdet saknar etikett (t.ex. myUplink-tid
                    // lagrad som tal) – behandla som vanligt tal.
                    numVal = rowValueNum;
                    // Använd inte value_str här – för myUplink är det rått ("15003min")
                }
            } else if (rowValueNum != null) {
                numVal = rowValueNum;
                // NIBE-statusbitar – visa avkodad text i tooltip
                if (NIBE_STATUS_PARAM_IDS.has(row.property.replace('p_', ''))) {
                    displayStr = `${decodeNibeStatus(rowValueNum)} (${rowValueNum})`;
                } else {
                    // value_str bara om den säger mer än talet självt.
                    // För myUplink är value_str bara tal+enhet – hoppa över.
                    const s = row.value_str;
                    if (s != null && isNaN(parseFloat(s))) {
                        // Ren text (t.ex. ett Zigbee-läge)
                        displayStr = s;
                    }
                }
            } else if (row.value_str != null) {
                const f = parseFloat(row.value_str);
                if (!isNaN(f)) numVal = f;
                displayStr = row.value_str;
            }

            if (numVal === undefined) continue;
            const key = makeSeriesKey(row.ieee_address, row.property);
            seriesSet.add(key);
            if (isEnum) enumSeriesKeys.add(key);
            if (!pointMap.has(ts)) pointMap.set(ts, { ts });
            pointMap.get(ts)![key] = numVal;
            if (displayStr) displayMap[`${ts}::${key}`] = displayStr;
        }
        const data = Array.from(pointMap.values()).sort((a, b) => a.ts - b.ts);

        return {
            chartData: data,
            chartSeries: Array.from(seriesSet),
            chartDisplayMap: displayMap,
            chartEnumSeries: enumSeriesKeys,
            chartEnumLabels: enumLabels,
        };
    }, [historyData, selectedDevices, selectedProperties]);

    // ── Bygg tabellen med senaste värden ─────────────────────────────────────

    const monitorRows = useMemo<MonitorRow[]>(() => {
        if (selectedDevices.size === 0 || selectedProperties.size === 0) return [];

        const best = new Map<string, { numericValue: number | null; displayStr: string; propName: string; enumValues?: { value: string; text: string }[]; timestamp: string; unit: string }>();
        for (const row of monitorData) {
            if (!selectedDevices.has(row.ieee_address)) continue;
            if (!selectedProperties.has(row.property)) continue;
            const key = `${row.ieee_address}::${row.property}`;
            const existing = best.get(key);
            if (existing && new Date(row.timestamp) <= new Date(existing.timestamp)) continue;

            const enumVals = row.enum_values ?? undefined;
            // DECIMAL-kolumner kommer som strängar från pg – gör om till tal.
            let numericValue: number | null = row.value_num != null ? Number(row.value_num) : null;
            let displayStr: string;
            if (enumVals?.length && numericValue != null) {
                const match = enumVals.find(e => Number(e.value) === numericValue);
                // Etikettens text, annars talet (inte rå strVal som kan ha enhet i sig)
                displayStr = match?.text ?? numericValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
            } else if (numericValue != null) {
                // NIBE-statusbitar för kretsläge
                if (NIBE_STATUS_PARAM_IDS.has(row.property.replace('p_', ''))) {
                    displayStr = `${decodeNibeStatus(numericValue)} (${numericValue})`;
                } else {
                    // Rent tal – myUplinks strVal har ofta enheten inbakad ("2min")
                    displayStr = numericValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
                }
            } else if (row.value_str != null) {
                displayStr = row.value_str;
            } else {
                displayStr = '—';
            }
            const propName = row.parameter_name ?? propLabel(row.property);
            const unit = row.parameter_unit ?? (() => {
                const lbl = PROPERTY_LABELS[row.property] ?? '';
                const m = /\(([^)]+)\)$/.exec(lbl.trim());
                return m ? m[1] : '';
            })();
            best.set(key, { numericValue, displayStr, propName, enumValues: enumVals, timestamp: row.timestamp, unit });
        }

        const rows: MonitorRow[] = [];
        for (const [key, { numericValue, displayStr, propName, enumValues, timestamp, unit }] of best) {
            const sepIdx = key.indexOf('::');
            const ieee = key.slice(0, sepIdx);
            const prop = key.slice(sepIdx + 2);
            const device = devices.find(d => d.id === ieee);
            rows.push({ ieee, name: device?.name ?? ieee, property: prop, propName, numericValue, displayStr, enumValues, timestamp, unit });
        }

        rows.sort((a, b) => a.name.localeCompare(b.name) || a.propName.localeCompare(b.propName));
        return rows;
    }, [monitorData, selectedDevices, selectedProperties, devices, propLabel]);

    // ── Tillgängliga mätvärden ───────────────────────────────────────────────

    // Unionen av de valda enheternas kända mätvärden: från enhetscachen,
    // från historiken (diagrammet) och från senaste värden.
    const availableMonitorProperties = useMemo<string[]>(() => {
        const exclude = MONITOR_EXCLUDED_PROPERTIES;
        if (selectedDevices.size === 0) return DEFAULT_PROPERTIES;
        const union = new Set<string>();
        let anyWithData = false;
        let allLoaded = true;
        for (const ieee of Array.from(selectedDevices)) {
            const props = devicePropertiesMap[ieee];
            if (props === undefined) { allLoaded = false; continue; }
            if (props.length > 0) { anyWithData = true; props.forEach(p => { if (!exclude.has(p)) union.add(p); }); }
        }
        const scanRows = (rows: DataRow[]) => {
            for (const row of rows) {
                if (!selectedDevices.has(row.ieee_address)) continue;
                if (!exclude.has(row.property)) union.add(row.property);
                anyWithData = true;
            }
        };
        scanRows(historyData);
        scanRows(monitorData);
        if (!allLoaded && !anyWithData) return DEFAULT_PROPERTIES; // hämtas fortfarande
        return anyWithData ? Array.from(union).sort() : DEFAULT_PROPERTIES;
    }, [selectedDevices, devicePropertiesMap, historyData, monitorData]);

    // Håller reda på vilka mätvärden som redan setts/lagts till automatiskt, så att en
    // omräkning (t.ex. efter att diagrammet laddats) inte lägger tillbaka bortvalda.
    const prevAvailablePropsRef = useRef<Set<string>>(new Set());

    // Nollställ när enhetsurvalet ändras så nya enheters mätvärden väljs från början.
    useEffect(() => {
        prevAvailablePropsRef.current = new Set();
    }, [selectedDevices]);

    // Välj automatiskt nya välkända Zigbee-mätvärden. Värmepumpens p_XXXXX-nycklar
    // väljs INTE automatiskt (skulle fylla diagrammet med 100+ kurvor).
    // Läggs bara till FÖRSTA gången de dyker upp – bortvalda kommer inte tillbaka.
    useEffect(() => {
        setSelectedProperties(prev => {
            const next = new Set(prev);
            let changed = false;
            for (const p of availableMonitorProperties) {
                if (!prevAvailablePropsRef.current.has(p) && !next.has(p)
                        && ALL_PROPERTIES.includes(p) && !MONITOR_EXCLUDED_PROPERTIES.has(p)) {
                    next.add(p);
                    changed = true;
                }
            }
            // Ta även bort dolda mätvärden om de råkar vara valda
            for (const p of MONITOR_EXCLUDED_PROPERTIES) {
                if (next.has(p)) { next.delete(p); changed = true; }
            }
            prevAvailablePropsRef.current = new Set(availableMonitorProperties);
            return changed ? next : prev;
        });
    }, [availableMonitorProperties]);

    const monitorPropsLoading = deviceProps.isAnyLoading(selectedDevices);

    // ── Val-hjälpare ─────────────────────────────────────────────────────────

    const toggleDevice = (id: string) => {
        setSelectedDevices(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleProperty = (p: string) => {
        setSelectedProperties(prev => {
            const next = new Set(prev);
            next.has(p) ? next.delete(p) : next.add(p);
            return next;
        });
    };

    const selectAllDevices = () => setSelectedDevices(new Set(filteredDevices.map(d => d.id)));
    const clearAllDevices = () => setSelectedDevices(new Set());
    const selectAllProperties = () => setSelectedProperties(new Set(availableMonitorProperties));
    const clearAllProperties = () => setSelectedProperties(new Set());

    return {
        filteredDevices, selectedDevices, selectedZone, setSelectedZone,
        toggleDevice, selectAllDevices, clearAllDevices,
        selectedProperties, availableMonitorProperties, monitorPropsLoading,
        propLabel, propNameMap, devicePropertiesMap,
        toggleProperty, selectAllProperties, clearAllProperties,
        templates: visibleTemplates, handleSaveTemplate, handleApplyTemplate, handleDeleteTemplate,
        monitorRows, monitorLoading, monitorError, loadMonitorData,
        logFrom, logTo, setLogFrom, setLogTo,
        historyLoading, historyError, historyData, loadHistoryData,
        ...chart,
    };
}
